import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from '../../api/client';
import API_CONFIG from '../../config/api';
import { ESPECIALIDADES_MEDICAS } from '../../constants/especialidadesMedicas';
import {
  SETORES_PRESTADOR,
  formatCNPJ,
  convertDateToISO,
  convertDateToBrazilian,
  parseEmpresaEspecialidade,
  buildEmpresaExibicao,
  toBool,
  prestadorToWizardForm
} from '../../utils/prestadorFormUtils';

const BACKEND_URL = API_CONFIG.BACKEND_URL;
const STEPS = ['Contrato', 'Empresa', 'Classificação', 'Termo aditivo'];

function mapItemForm(it) {
  return {
    descricao: it.descricao || '',
    unidade: it.unidade || 'UND',
    quantidade: it.quantidade ?? '',
    valor_unitario: it.valor_unitario ?? '',
    total:
      it.total != null
        ? it.total
        : (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0),
  };
}

/**
 * Herda itens: aditivos anteriores (maior número com itens) → senão itens do contrato base.
 * @param {string} numeroContrato
 * @param {number|null} antesDoNumero — se informado, só considera aditivos com numeroAditivo < este
 * @param {number|null} contratoId
 */
async function herdarItensAnteriores(numeroContrato, antesDoNumero = null, contratoId = null) {
  let itensHerdados = [];
  try {
    const resAditivos = await apiFetch(
      `/api/aditivos?numeroContrato=${encodeURIComponent(numeroContrato)}`
    );
    if (resAditivos.ok) {
      let aditivos = await resAditivos.json();
      if (Array.isArray(aditivos) && aditivos.length) {
        aditivos = [...aditivos].sort(
          (a, b) => (Number(a.numeroAditivo) || 0) - (Number(b.numeroAditivo) || 0)
        );
        const limite = antesDoNumero != null ? Number(antesDoNumero) : null;
        for (let i = aditivos.length - 1; i >= 0; i -= 1) {
          const num = Number(aditivos[i].numeroAditivo) || 0;
          if (limite != null && num >= limite) continue;
          try {
            const resIt = await apiFetch(`/api/aditivos/${aditivos[i].id}/itens`);
            if (resIt.ok) {
              const dataIt = await resIt.json();
              if (Array.isArray(dataIt) && dataIt.length) {
                return dataIt.map(mapItemForm);
              }
            }
          } catch {
            /* tenta o anterior */
          }
        }
      }
    }
  } catch {
    /* segue para contrato */
  }

  if (contratoId) {
    try {
      const resItC = await apiFetch(`/api/prestadores/${contratoId}/itens`);
      if (resItC.ok) {
        const dataItC = await resItC.json();
        if (Array.isArray(dataItC) && dataItC.length) {
          itensHerdados = dataItC.map(mapItemForm);
        }
      }
    } catch {
      /* ignore */
    }
  }
  return itensHerdados;
}

const emptyForm = {
  numeroContrato: '',
  numeroAditivo: '',
  empresa: '',
  cnpj: '',
  email: '',
  assinatura: '',
  vigenciaInicio: '',
  vigenciaFim: '',
  valorMes: '',
  setor: '',
  objeto: '',
  parcelas: 12,
  eventual: false,
  corporativo: false,
  servicoMedico: false,
  especialidade: '',
  horasMensais: '',
  horasSemanais: '',
  quantidadeMedicos: '',
  terceirizada: false,
  quantidadeFuncionarios: '',
  locacao: false
};

export default function AditivoWizardDialog({
  open,
  onClose,
  onSaved,
  unidadeSelecionada,
  aditivoEdit = null,
  prestadorRef = null,
  numeroContratoInicial = '',
  podeRatear = false,
  unidades = []
}) {
  const isEdit = Boolean(aditivoEdit?.id);
  const steps = podeRatear ? [...STEPS, 'Unidades'] : STEPS;
  const unidadesStep = podeRatear ? steps.length - 1 : -1;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [itens, setItens] = useState([]);
  const [itensHerdadosHint, setItensHerdadosHint] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rateioModo, setRateioModo] = useState('sem_rateio');
  const [unidadeMatriz, setUnidadeMatriz] = useState('');
  const [rateios, setRateios] = useState([]);
  const [contratoRefLocal, setContratoRefLocal] = useState(null);
  const refRateio = prestadorRef || contratoRefLocal;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'cnpj') v = formatCNPJ(v);
    setForm((p) => ({ ...p, [name]: v }));
  };

  const empresaExibicao = () => buildEmpresaExibicao(form);

  const fetchEmailPortal = async (cnpj) => {
    const cnpjNorm = String(cnpj || '').replace(/\D/g, '');
    if (cnpjNorm.length < 11) return '';
    try {
      const res = await apiFetch('/api/calendario/prestadores-destino');
      if (!res.ok) return '';
      const lista = await res.json();
      const match = (Array.isArray(lista) ? lista : []).find(
        (p) => String(p.cnpj || '').replace(/\D/g, '') === cnpjNorm
      );
      return match?.email || '';
    } catch {
      return '';
    }
  };

  const carregarContrato = async (numeroContrato, keepAditivoNum = null) => {
    const numero = String(numeroContrato || '').trim();
    if (!numero) return;
    setLoading(true);
    setError('');
    try {
      const resContrato = await fetch(
        `${BACKEND_URL}/api/prestadores?numeroContrato=${encodeURIComponent(numero)}&unidade=${encodeURIComponent(unidadeSelecionada?.codigo || '')}`
      );
      const contratos = await resContrato.json();
      const lista = Array.isArray(contratos) ? contratos : contratos ? [contratos] : [];
      // Preferir matriz (não a cópia rateada com valor 0)
      const contrato =
        lista.find((c) => !c.eh_rateio_filho) ||
        lista.find((c) => c.eh_contrato_matriz) ||
        lista[0];
      if (
        !contrato ||
        !Object.keys(contrato).length ||
        (contrato.unidade && unidadeSelecionada?.codigo && contrato.unidade !== unidadeSelecionada.codigo)
      ) {
        setError('Contrato não encontrado para a unidade selecionada.');
        return;
      }
      setContratoRefLocal(contrato);

      let proximoAditivo = keepAditivoNum || '1';
      if (!keepAditivoNum) {
        const resAditivos = await fetch(
          `${BACKEND_URL}/api/aditivos?numeroContrato=${encodeURIComponent(numero)}`
        );
        const aditivos = await resAditivos.json();
        if (Array.isArray(aditivos) && aditivos.length > 0) {
          const last = aditivos[aditivos.length - 1];
          proximoAditivo = String(Number(last.numeroAditivo) + 1 || 1);
        }
      }

      const itensHerdados = !isEdit
        ? await herdarItensAnteriores(numero, null, contrato.id)
        : [];

      const parsed = prestadorToWizardForm(contrato, {});
      const email = (await fetchEmailPortal(contrato.cnpj)) || parsed.email;

      setForm((f) => ({
        ...f,
        numeroContrato: numero,
        numeroAditivo: String(keepAditivoNum || proximoAditivo),
        empresa: parsed.empresa,
        cnpj: parsed.cnpj,
        email,
        assinatura: parsed.assinatura,
        vigenciaInicio: parsed.vigenciaInicio,
        vigenciaFim: parsed.vigenciaFim,
        valorMes: parsed.valorMes,
        setor: parsed.setor,
        objeto: parsed.objeto,
        parcelas: contrato.parcelas || 12,
        eventual: parsed.eventual,
        corporativo: parsed.corporativo,
        servicoMedico: parsed.servicoMedico,
        especialidade: parsed.especialidade,
        horasMensais: parsed.horasMensais,
        horasSemanais: parsed.horasSemanais,
        quantidadeMedicos: parsed.quantidadeMedicos,
        terceirizada: parsed.terceirizada,
        quantidadeFuncionarios: parsed.quantidadeFuncionarios,
        locacao: parsed.locacao
      }));
      if (!isEdit) {
        setItens(itensHerdados);
        setItensHerdadosHint(itensHerdados.length > 0);
      }
    } catch {
      setError('Erro ao buscar contrato.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError('');
    setItens([]);
    setItensHerdadosHint(false);

    if (isEdit && aditivoEdit) {
      const { base, especialidade } = parseEmpresaEspecialidade(aditivoEdit.empresa);
      const ref = prestadorRef || {};
      (async () => {
        setLoading(true);
        const email =
          (await fetchEmailPortal(aditivoEdit.cnpj || ref.cnpj)) || '';
        setForm({
          ...emptyForm,
          numeroContrato: aditivoEdit.numeroContrato || ref.numeroContrato || '',
          numeroAditivo: String(aditivoEdit.numeroAditivo || ''),
          empresa: base || aditivoEdit.empresa || '',
          cnpj: formatCNPJ(aditivoEdit.cnpj || ref.cnpj || ''),
          email,
          assinatura: convertDateToISO(aditivoEdit.assinatura),
          vigenciaInicio: convertDateToISO(aditivoEdit.vigenciaInicio),
          vigenciaFim: convertDateToISO(aditivoEdit.vigenciaFim),
          valorMes: aditivoEdit.valorMes || '',
          setor: aditivoEdit.setor || ref.setor || '',
          objeto: aditivoEdit.objeto || '',
          parcelas: aditivoEdit.parcelas || 12,
          eventual: toBool(ref.eventual),
          corporativo: toBool(ref.corporativo),
          servicoMedico: toBool(ref.servicoMedico),
          especialidade: ref.especialidade || especialidade || '',
          horasMensais: ref.horasMensais || '',
          horasSemanais: ref.horasSemanais || '',
          quantidadeMedicos: ref.quantidadeMedicos || '',
          terceirizada: toBool(ref.terceirizada),
          quantidadeFuncionarios: ref.quantidadeFuncionarios || '',
          locacao: toBool(ref.locacao)
        });
        try {
          const resItens = await apiFetch(`/api/aditivos/${aditivoEdit.id}/itens`);
          let loaded = [];
          if (resItens.ok) {
            const data = await resItens.json();
            loaded = Array.isArray(data) ? data.map(mapItemForm) : [];
          }
          if (loaded.length) {
            setItens(loaded);
            setItensHerdadosHint(false);
          } else {
            const numero = aditivoEdit.numeroContrato || ref.numeroContrato || '';
            const numAd = Number(aditivoEdit.numeroAditivo) || null;
            let contratoId = ref.id || null;
            if (!contratoId && numero) {
              try {
                const resC = await apiFetch(
                  `/api/prestadores?numeroContrato=${encodeURIComponent(numero)}&unidade=${encodeURIComponent(unidadeSelecionada?.codigo || '')}`
                );
                if (resC.ok) {
                  const lista = await resC.json();
                  const c = Array.isArray(lista) ? lista[0] : lista;
                  contratoId = c?.id || null;
                }
              } catch {
                /* ignore */
              }
            }
            const herdados = await herdarItensAnteriores(numero, numAd, contratoId);
            setItens(herdados);
            setItensHerdadosHint(herdados.length > 0);
          }
        } catch {
          setItens([]);
        }
        setLoading(false);
      })();
      return;
    }

    setForm({ ...emptyForm, numeroContrato: numeroContratoInicial || '' });
    if (numeroContratoInicial) {
      carregarContrato(numeroContratoInicial);
    }
  }, [open, aditivoEdit, isEdit, numeroContratoInicial, prestadorRef]);

  useEffect(() => {
    if (!open || !podeRatear) return;
    const base = (unidades || [])
      .filter((u) => u?.codigo && String(u.codigo).toUpperCase() !== 'CORPORATIVO')
      .map((u) => ({
        unidade: u.codigo,
        nome: u.nome || u.codigo,
        selected: false,
        percentual: '',
        valorMesUnidade: '',
      }));
    setRateios(base);
    setUnidadeMatriz(refRateio?.unidade || unidadeSelecionada?.codigo || '');
    setRateioModo('sem_rateio');
    const matrizId = refRateio?.eh_rateio_filho
      ? refRateio.contrato_matriz_id
      : refRateio?.id;
    if (!matrizId) return;
    let cancelled = false;
    (async () => {
      const resR = await apiFetch(`/api/prestadores/${matrizId}/rateios`);
      if (!resR.ok || cancelled) return;
      const dataR = await resR.json();
      const map = Object.fromEntries((dataR.rateios || []).map((r) => [r.unidade, r]));
      if (dataR.rateios?.[0]?.modo) setRateioModo(dataR.rateios[0].modo);
      setRateios((prev) =>
        prev.map((row) => {
          const r = map[row.unidade];
          if (!r) return row;
          return {
            ...row,
            selected: true,
            percentual: r.percentual != null ? String(r.percentual) : '',
            valorMesUnidade: r.valorMesUnidade || '',
          };
        })
      );
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, podeRatear, unidades, refRateio, unidadeSelecionada]);

  const validarPasso = () => {
    if (step === 0) {
      if (!form.numeroContrato || !form.numeroAditivo) {
        setError('Informe o contrato. O número do aditivo é gerado automaticamente.');
        return false;
      }
    }
    if (step === 1) {
      if (!form.empresa || !form.cnpj) {
        setError('Preencha empresa e CNPJ.');
        return false;
      }
      const emailNorm = form.email?.trim();
      if (emailNorm && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailNorm)) {
        setError('E-mail inválido.');
        return false;
      }
    }
    if (step === 2) {
      if (form.servicoMedico && !form.especialidade) {
        setError('Selecione a especialidade médica.');
        return false;
      }
      if (form.servicoMedico && (!form.horasMensais || !form.horasSemanais || !form.quantidadeMedicos)) {
        setError('Preencha horas e quantidade de médicos.');
        return false;
      }
      if (form.terceirizada && !form.quantidadeFuncionarios) {
        setError('Informe a quantidade de funcionários.');
        return false;
      }
    }
    if (step === 3) {
      if (
        !form.assinatura ||
        !form.vigenciaInicio ||
        !form.vigenciaFim ||
        !form.valorMes ||
        !form.setor ||
        !form.objeto
      ) {
        setError('Preencha todos os campos do termo aditivo.');
        return false;
      }
    }
    setError('');
    return true;
  };

  const avancar = () => {
    if (!validarPasso()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const voltar = () => setStep((s) => Math.max(s - 1, 0));

  const handleClose = () => {
    setStep(0);
    setForm(emptyForm);
    setItens([]);
    setError('');
    setContratoRefLocal(null);
    onClose();
  };

  const buildCamposEditados = () => ({
    empresa: empresaExibicao(),
    cnpj: form.cnpj.replace(/\D/g, ''),
    assinatura: convertDateToBrazilian(form.assinatura),
    vigenciaInicio: convertDateToBrazilian(form.vigenciaInicio),
    vigenciaFim: convertDateToBrazilian(form.vigenciaFim),
    valorMes: form.valorMes,
    setor: form.setor,
    objeto: form.objeto,
    parcelas: form.parcelas || 12,
    eventual: form.eventual,
    corporativo: form.corporativo,
    servicoMedico: form.servicoMedico,
    terceirizada: form.terceirizada,
    horasMensais: form.horasMensais,
    horasSemanais: form.horasSemanais,
    quantidadeMedicos: form.quantidadeMedicos,
    quantidadeFuncionarios: form.quantidadeFuncionarios,
    especialidade: form.servicoMedico ? form.especialidade : '',
    locacao: form.locacao,
    email: form.email
  });

  const finalizar = async () => {
    if (!validarPasso()) return;
    setSaving(true);
    setError('');
    try {
      const camposEditados = buildCamposEditados();
      const selecionados = rateios.filter((r) => r.selected);
      const body = {
        numeroContrato: form.numeroContrato,
        camposEditados,
        itens,
        ...(podeRatear && selecionados.length
          ? {
              unidadeMatriz: unidadeMatriz || unidadeSelecionada?.codigo || null,
              rateios: selecionados.map((r) => ({
                unidade: r.unidade,
                modo: rateioModo,
                percentual: rateioModo === 'percentual' ? Number(String(r.percentual).replace(',', '.')) || 0 : undefined,
                valorMesUnidade: rateioModo === 'valor' ? r.valorMesUnidade : undefined,
              })),
            }
          : {}),
      };

      const url = isEdit
        ? `${BACKEND_URL}/api/aditivos/${aditivoEdit.id}`
        : `${BACKEND_URL}/api/aditivos`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao salvar aditivo');
      }

      const emailNorm = form.email?.trim();
      await apiFetch('/api/admin/prestadores-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razaoSocial: form.empresa,
          cnpj: form.cnpj.replace(/\D/g, ''),
          email: emailNorm || undefined,
          unidade: unidadeSelecionada?.codigo || null
        })
      }).catch(() => {});

      onSaved?.({ edit: isEdit });
      handleClose();
    } catch (e) {
      setError(e.message || 'Erro ao salvar aditivo.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    setItens([...itens, { descricao: '', unidade: 'KG', quantidade: '', valor_unitario: '', total: 0 }]);
  };

  const handleRemoveItem = (index) => {
    setItens(itens.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const novos = [...itens];
    novos[index] = { ...novos[index], [field]: value };
    if (field === 'quantidade' || field === 'valor_unitario') {
      const q = parseFloat(novos[index].quantidade) || 0;
      const v = parseFloat(novos[index].valor_unitario) || 0;
      novos[index].total = q * v;
    }
    setItens(novos);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar aditivo — assistente' : 'Novo aditivo — assistente'}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Carregando dados...</Typography>
          </Box>
        )}

        {step === 0 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Nº Contrato"
              name="numeroContrato"
              value={form.numeroContrato}
              onChange={handleChange}
              onBlur={() => !isEdit && carregarContrato(form.numeroContrato)}
              fullWidth
              required
              disabled={isEdit || loading}
            />
            <TextField label="Nº Aditivo" value={form.numeroAditivo} fullWidth disabled />
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              Ao informar o contrato, os dados atuais são carregados como base para o aditivo.
            </Typography>
          </Box>
        )}

        {step === 1 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Empresa"
              name="empresa"
              value={form.empresa}
              onChange={handleChange}
              fullWidth
              required
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField label="CNPJ" name="cnpj" value={form.cnpj} onChange={handleChange} fullWidth required />
            <TextField
              label="E-mail"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              helperText="Opcional. Preenchido automaticamente se já cadastrado no portal."
            />
          </Box>
        )}

        {step === 2 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={<Checkbox name="eventual" checked={form.eventual} onChange={handleChange} />}
              label="Eventual"
            />
            <FormControlLabel
              control={<Checkbox name="corporativo" checked={form.corporativo} onChange={handleChange} />}
              label="Corporativo"
            />
            <FormControlLabel
              control={<Checkbox name="servicoMedico" checked={form.servicoMedico} onChange={handleChange} />}
              label="Serviços médicos"
            />
            {form.servicoMedico && (
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField
                  select
                  label="Especialidade"
                  name="especialidade"
                  value={form.especialidade}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {ESPECIALIDADES_MEDICAS.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Horas mensais"
                  name="horasMensais"
                  type="number"
                  value={form.horasMensais}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Horas semanais"
                  name="horasSemanais"
                  type="number"
                  value={form.horasSemanais}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Quantidade de médicos"
                  name="quantidadeMedicos"
                  type="number"
                  value={form.quantidadeMedicos}
                  onChange={handleChange}
                  fullWidth
                />
              </Box>
            )}
            <FormControlLabel
              control={<Checkbox name="terceirizada" checked={form.terceirizada} onChange={handleChange} />}
              label="Terceirizada"
            />
            {form.terceirizada && (
              <TextField
                label="Quantidade de funcionários"
                name="quantidadeFuncionarios"
                type="number"
                value={form.quantidadeFuncionarios}
                onChange={handleChange}
                fullWidth
              />
            )}
            <FormControlLabel
              control={<Checkbox name="locacao" checked={form.locacao} onChange={handleChange} />}
              label="Locação"
            />
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
              <TextField
                label="Data de assinatura"
                name="assinatura"
                type="date"
                value={form.assinatura}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Início vigência"
                name="vigenciaInicio"
                type="date"
                value={form.vigenciaInicio}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Fim vigência"
                name="vigenciaFim"
                type="date"
                value={form.vigenciaFim}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              <TextField
                label="Valor/mês"
                name="valorMes"
                value={form.valorMes}
                onChange={handleChange}
                fullWidth
                required
              />
              <TextField
                select
                label="Setor"
                name="setor"
                value={form.setor}
                onChange={handleChange}
                fullWidth
                required
              >
                {SETORES_PRESTADOR.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Parcelas"
                name="parcelas"
                type="number"
                value={form.parcelas}
                onChange={handleChange}
                inputProps={{ min: 1, max: 12 }}
                fullWidth
              />
              <TextField
                label="Objeto"
                name="objeto"
                value={form.objeto}
                onChange={handleChange}
                multiline
                minRows={2}
                fullWidth
                required
                sx={{ gridColumn: '1 / -1' }}
              />
            </Box>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">Itens do aditivo</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem}>
                  Adicionar item
                </Button>
              </Box>
              {itensHerdadosHint && itens.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Itens pré-carregados do aditivo/contrato anterior para conferência. Ajuste valores se necessário e salve.
                </Alert>
              )}
              {itens.map((item, index) => (
                <Card key={index} sx={{ mb: 2, p: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">Item {index + 1}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Descrição"
                        value={item.descricao}
                        onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        select
                        label="Unidade"
                        value={item.unidade}
                        onChange={(e) => handleItemChange(index, 'unidade', e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="KG">KG</MenuItem>
                        <MenuItem value="UND">UND</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Quantidade"
                        type="number"
                        value={item.quantidade}
                        onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Valor unitário"
                        type="number"
                        value={item.valor_unitario}
                        onChange={(e) => handleItemChange(index, 'valor_unitario', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
              {itens.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum item adicionado (opcional).
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {step === unidadesStep && unidadesStep >= 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info">
              {rateioModo === 'sem_rateio'
                ? 'Marque as unidades que também terão este contrato corporativo. Sem percentual e sem fatia. O valor conta na nota de cada unidade. Na matriz: Nota nº XXX - UNIDADE.'
                : 'Ajuste o rateio das unidades deste contrato.'}
            </Alert>
            <TextField
              select
              label="Unidade matriz"
              value={unidadeMatriz}
              onChange={(e) => setUnidadeMatriz(e.target.value)}
              fullWidth
            >
              {rateios.map((r) => (
                <MenuItem key={r.unidade} value={r.unidade}>
                  {r.nome} ({r.unidade})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Modo do rateio"
              value={rateioModo}
              onChange={(e) => setRateioModo(e.target.value)}
              fullWidth
            >
              <MenuItem value="percentual">Percentual (%)</MenuItem>
              <MenuItem value="valor">Valor fixo (R$)</MenuItem>
              <MenuItem value="sem_rateio">Sem rateio</MenuItem>
            </TextField>
            {rateios.map((row) => (
              <Paper key={row.unidade} variant="outlined" sx={{ p: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={row.selected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRateios((prev) =>
                          prev.map((r) => (r.unidade === row.unidade ? { ...r, selected: checked } : r))
                        );
                      }}
                    />
                  }
                  label={`${row.nome} (${row.unidade})${unidadeMatriz === row.unidade ? ' — Matriz' : ''}`}
                />
                {row.selected && rateioModo !== 'sem_rateio' && (
                  <TextField
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    label={rateioModo === 'percentual' ? 'Percentual %' : 'Valor da fatia'}
                    value={rateioModo === 'percentual' ? row.percentual : row.valorMesUnidade}
                    onChange={(e) =>
                      setRateios((prev) =>
                        prev.map((r) =>
                          r.unidade === row.unidade
                            ? {
                                ...r,
                                [rateioModo === 'percentual' ? 'percentual' : 'valorMesUnidade']: e.target.value,
                              }
                            : r
                        )
                      )
                    }
                  />
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        {step > 0 && (
          <Button onClick={voltar} disabled={saving}>
            Voltar
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button variant="contained" onClick={avancar} disabled={loading}>
            Próximo
          </Button>
        ) : (
          <Button variant="contained" onClick={finalizar} disabled={saving}>
            {saving ? 'Salvando...' : isEdit ? 'Salvar aditivo' : 'Registrar aditivo'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
